import React from 'react'
import Box from '@mui/material/Box'

export type AriaLivePoliteness = 'polite' | 'assertive'

interface UseAriaLiveResult {
  announce: (message: string, politeness?: AriaLivePoliteness) => void
  ariaLive: React.ReactElement | null
}

export const useAriaLive = (
  initialPoliteness: AriaLivePoliteness = 'polite',
): UseAriaLiveResult => {
  const [message, setMessage] = React.useState('')
  const [politeness, setPoliteness] = React.useState<AriaLivePoliteness>(initialPoliteness)

  const announce = React.useCallback((next: string, nextPoliteness?: AriaLivePoliteness) => {
    if (nextPoliteness) setPoliteness(nextPoliteness)
    setMessage(next)
  }, [])

  const ariaLive = React.useMemo(
    () => (
      <Box
        aria-atomic="true"
        aria-live={politeness}
        role={politeness === 'assertive' ? 'alert' : 'status'}
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {message}
      </Box>
    ),
    [message, politeness],
  )

  return { announce, ariaLive }
}
